import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Layers, Plus, RefreshCw, Trophy, Zap, BarChart3, History, Edit3, Play, Search, Trash2, Star } from 'lucide-react';

// ── ПАЛИТРА ЭКРАНА «СЕГОДНЯ» → §3.1 (Ф1, quick-260909-jl8) ─────────────────
// Откат в каждой строке — ПРЕЖНИЙ литерал ЭТОГО ЖЕ места. На fitenko токен
// объявлен (styles/fitenko-tokens.css, селектор html.fitenko) и берётся он;
// на MLG и ELN токена НЕТ — берётся откат, то есть РОВНО прежний цвет.
// 🔴 Значения отката менять нельзя: ими доказывается неизменность чужих арендаторов.
// 🔴 Голый var(--ink) запрещён: у MLG он разрешается в ПУСТОТУ (отсутствие цвета),
//    а не в другой цвет — объявление становится недействительным.
// Класс C (подложки чипов) — по решению владельца от 2026-09-09, вариант B:
// подложка выводится правилом color-mix(in srgb, АКЦЕНТ 14%, --surface); правило
// объявлено ОДИН раз в pages/Dashboard.css под селектором html.fitenko. Бирюза —
// вариант T1: своего тона в §3.1 нет, берётся --green-accent.
const P = {
    canvas:       'var(--canvas, #f8fafc)',  // класс A
    ink:          'var(--ink, #1e293b)',  // класс A
    inkDeep:      'var(--ink, #0f172a)',  // класс A
    line:         'var(--line, #e2e8f0)',  // класс A
    lineStrong:   'var(--line, #cbd5e1)',  // класс A
    muted:        'var(--ink-muted, #64748b)',  // класс A
    mutedSoft:    'var(--ink-muted, #94a3b8)',  // класс A
    surfaceShort: 'var(--surface, #fff)',  // класс A
    amber11:      'var(--amber-accent, #d29462)',  // класс B
    amber8:       'var(--amber-accent, #854d0e)',  // класс B
    blue1:        'var(--blue-accent, #3b82f6)',  // класс B
    blue7:        'var(--blue-accent, #48628f)',  // класс B
    blue9:        'var(--blue-accent, #4f46e5)',  // класс B
    green3:       'var(--green-accent, #166534)',  // класс B
    green5:       'var(--green-accent, #7fc684)',  // класс B
    red2:         'var(--red-accent, #ef4444)',  // класс B
    red3:         'var(--red-accent, #991b1b)',  // класс B
    red4:         'var(--red-accent, #e11d48)',  // класс B
    red7:         'var(--red-accent, #e37275)',  // класс B
    chipAmber1:   'var(--chip-amber-bg, #fef3c7)',  // класс C
    chipBlue1:    'var(--chip-blue-bg, #eff6ff)',  // класс C
    chipBlue5:    'var(--chip-blue-bg, #dae9fe)',  // класс C
    chipGreen1:   'var(--chip-green-bg, #f0fdf4)',  // класс C
    chipGreen2:   'var(--chip-green-bg, #dcfce7)',  // класс C
    chipGreen5:   'var(--chip-green-bg, #bbf7d0)',  // класс C
    chipRed1:     'var(--chip-red-bg, #fee2e2)',  // класс C
    chipRed2:     'var(--chip-red-bg, #fecaca)',  // класс C
    chipRed4:     'var(--chip-red-bg, #fff1f2)',  // класс C
};




// Helper to extract City from Address string (Duplicated from Dashboard for isolation)
const getCityFromAddress = (address) => {
    if (!address) return "New Location";
    const parts = address.split(',');
    if (parts.length >= 2) {
        return parts[1].trim();
    }
    return address.length > 20 ? address.substring(0, 20) + '...' : address;
};

const OfficeLocationsList = ({
    profiles,
    loading,
    onAuditAll,
    onAddLocation,
    onOpenEditor,
    onAuditProfile,
    onSelectProfile,
    onImprove, // [NEW] Handler for improvement actions
    onSetMain, // [NEW] Handler for setting main location
    onDelete   // [NEW] Handler for deleting location
}) => {
    const navigate = useNavigate();

    // [STUB] Handlers replaced by props
    const handleDelete = (id, e) => {
        e.stopPropagation();
        if (onDelete) onDelete(id);
    };

    const handleSetMain = (id, e) => {
        e.stopPropagation();
        if (onSetMain) onSetMain(id);
    };

    // [LOGIC] Sort profiles: Main Location (isMain=true) always first
    // Note: Supports both camelCase (Optimistic) and snake_case (API)
    const sortedProfiles = [...(profiles || [])].sort((a, b) => {
        const isMainA = a.isMain || a.is_main;
        const isMainB = b.isMain || b.is_main;
        return (isMainB === true ? 1 : 0) - (isMainA === true ? 1 : 0);
    });

    return (
        <div className="card" style={{ height: '100%', display: 'flex', flexDirection: 'column', border: 'none', boxShadow: 'none' }}>
            {/* PINNED HEADER */}
            <div className="card-header" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px',
                borderBottom: `1px solid ${P.line}`,
                flexShrink: 0
            }}>
                <h2 className="card-title" style={{ margin: 0, fontSize: '1.1rem' }}>
                    <MapPin size={20} className="text-blue-600" /> Office Locations Management
                </h2>

                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={onAuditAll} style={{ color: P.blue9, borderColor: P.blue9 }}>
                        <Layers size={14} style={{ marginRight: '6px' }} /> Audit All
                    </button>

                    <button className="btn btn-primary btn-sm" onClick={onAddLocation}>
                        <Plus size={14} /> Add Location
                    </button>
                </div>
            </div>

            {/* SCROLLABLE LIST AREA (Unified Style) */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                maxHeight: '450px',
                padding: '20px',
                background: P.canvas
            }} className="smart-feed-scroll">

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: P.muted }}>
                        <RefreshCw size={30} className="spin-animation" style={{ marginBottom: '10px' }} />
                        <div>Loading Network Data...</div>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                        {sortedProfiles.map((p, index) => {
                            const isMain = p.isMain || p.is_main;
                            return (
                                <div
                                    key={p.id}
                                    className="profile-row-modern"
                                    onClick={() => onOpenEditor(p, 'truth')}
                                    style={{
                                        border: isMain ? `1px solid ${P.blue1}` : `1px solid ${P.line}`,
                                        background: isMain ? P.chipBlue1 : 'white'
                                    }}
                                >
                                    <div className="profile-info-modern" style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>

                                        {/* NUMBER BADGE */}
                                        <div className="profile-avatar-modern" style={{
                                            width: '40px', height: '40px',
                                            background: isMain ? P.blue1 : P.chipBlue5,
                                            color: isMain ? 'white' : P.blue7,
                                            borderRadius: '8px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontWeight: 'bold',
                                            fontSize: '1rem',
                                            flexShrink: 0
                                        }}>
                                            {isMain ? <Star size={18} fill="white" /> : `#${index + 1}`}
                                        </div>

                                        <div className="profile-details">
                                            <h3 className="profile-name-modern" style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: P.ink, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {getCityFromAddress(p.address)}
                                                {isMain && (
                                                    <span style={{ fontSize: '0.7rem', background: P.blue1, color: 'white', padding: '2px 6px', borderRadius: '4px', textTransform: 'uppercase' }}>
                                                        Main HQ
                                                    </span>
                                                )}
                                            </h3>

                                            <div className="profile-address-modern" style={{ fontSize: '0.8rem', color: P.muted }}>
                                                {p.address ? <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><MapPin size={12} /> {p.address}</span> : <span style={{ opacity: 0.7 }}>No address set</span>}
                                            </div>

                                            <div className="profile-metrics-row" style={{ display: 'flex', gap: '15px', marginTop: '6px', fontSize: '0.75rem', color: P.muted, flexWrap: 'wrap' }}>
                                                {/* 🔴 260916-wau: различаем «значения нет» и измеренный ноль.
                                                    Проверка ДОЛЖНА быть на null/undefined, а НЕ на falsy:
                                                    0 замеров — это ОТВЕТ, и он обязан печататься как «0 Audits».
                                                    При null раньше выходило «📊  Audits» — пустое место на месте ответа. */}
                                                <span>📊 {(p.total_audits === null || p.total_audits === undefined)
                                                    ? 'No audits yet'
                                                    : `${p.total_audits} Audits`}</span>
                                                {p.attack_win_rate !== undefined && (
                                                    <span style={{
                                                        color: p.attack_win_rate >= 50 ? P.green5 : (p.attack_win_rate >= 20 ? P.amber11 : P.red7),
                                                        fontWeight: '700',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                    title="Attack-phase win rate: % of battles where we ranked top-3 across non-Google-Organic engines. Matches Top 3 Strongest panel."
                                                    >
                                                        {p.attack_win_rate >= 50 ? <Trophy size={12} /> : <Zap size={12} />}
                                                        ⚔️ {p.attack_win_rate}% Attack
                                                    </span>
                                                )}
                                                {p.win_rate !== undefined && (
                                                    <span style={{
                                                        color: P.mutedSoft,
                                                        fontWeight: '600',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                    title="Organic win rate: BattleSession.outcome='WIN' / total sessions. Legacy metric — see Russel for context if it looks stale."
                                                    >
                                                        🪶 {p.win_rate !== null ? `${p.win_rate}%` : '—'} Organic
                                                    </span>
                                                )}
                                            </div>
                                            {p.engine_breakdown && Object.keys(p.engine_breakdown).length > 0 && (
                                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}
                                                     title="Per-engine attack-phase win rate (rank ≤ 3). Hover any badge for raw counts.">
                                                    {Object.entries(p.engine_breakdown)
                                                        .sort((a, b) => ((b[1].win_rate !== null && b[1].win_rate !== undefined) ? b[1].win_rate : -1) - ((a[1].win_rate !== null && a[1].win_rate !== undefined) ? a[1].win_rate : -1))
                                                        .map(([engine, stats]) => {
                                                            const labels = {
                                                                gemini: 'Gemini', gpt4: 'GPT-4', deepseek_r1: 'DeepSeek',
                                                                grok: 'Grok', claude: 'Claude', copilot: 'Copilot',
                                                                perplexity: 'Perplexity',
                                                            };
                                                            const wr = stats.win_rate;
                                                            const wrText = wr !== null && wr !== undefined ? `${wr}%` : '—';
                                                            const bg = wr !== null && wr !== undefined ? (wr >= 50 ? P.chipGreen2 : (wr >= 20 ? P.chipAmber1 : P.chipRed1)) : (P.chipNeutral || 'var(--surface-sunken)');
                                                            const fg = wr !== null && wr !== undefined ? (wr >= 50 ? P.green3 : (wr >= 20 ? P.amber8 : P.red3)) : (P.mutedSoft || 'var(--text-muted)');
                                                            return (
                                                                <span key={engine} style={{
                                                                    fontSize: '10px', padding: '2px 7px',
                                                                    borderRadius: '8px', background: bg, color: fg,
                                                                    fontWeight: '600',
                                                                }}
                                                                title={`${engine}: ${stats.wins}/${stats.total} top-3 battles`}>
                                                                    {labels[engine] || engine} {wrText}
                                                                </span>
                                                            );
                                                        })}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="profile-actions" style={{ display: 'flex', gap: '8px' }}>

                                        {/* [NEW] Contextual Improve Action (Integrated Alert) */}
                                        {p.win_rate !== undefined && p.win_rate !== null && p.win_rate < 30 && (
                                            <div className="tooltip-wrapper">
                                                <button
                                                    className="btn-icon-outline"
                                                    onClick={(e) => { e.stopPropagation(); if (onImprove) onImprove(p); }}
                                                    style={{
                                                        borderColor: P.chipRed2,
                                                        color: P.red4,
                                                        background: P.chipRed4,
                                                        boxShadow: `0 0 0 1px ${P.chipRed2}`
                                                    }}
                                                >
                                                    <Zap size={16} className="pulse-animation-soft" fill="currentColor" />
                                                </button>
                                                <span className="tooltip">Fix Issues</span>
                                            </div>
                                        )}

                                        {/* [NEW] Set Main Location Action */}
                                        {!isMain && (
                                            <div className="tooltip-wrapper">
                                                <button
                                                    className="btn-icon-outline"
                                                    onClick={(e) => handleSetMain(p.id, e)}
                                                    style={{ color: P.lineStrong }}
                                                >
                                                    <Star size={16} />
                                                </button>
                                                <span className="tooltip">Make Main</span>
                                            </div>
                                        )}

                                        <div className="tooltip-wrapper">
                                            <button className="btn-icon-outline" onClick={(e) => { e.stopPropagation(); onOpenEditor(p, 'analytics'); }}>
                                                <BarChart3 size={16} style={{ stroke: P.blue1 }} />
                                            </button>
                                            <span className="tooltip">Statistics</span>
                                        </div>

                                        <div className="tooltip-wrapper">
                                            <button className="btn-icon-outline" onClick={(e) => { e.stopPropagation(); onOpenEditor(p, 'chat'); }}>
                                                <History size={16} style={{ stroke: P.blue1 }} />
                                            </button>
                                            <span className="tooltip">History</span>
                                        </div>

                                        <div className="tooltip-wrapper">
                                            <button className="btn-icon-outline" onClick={(e) => { e.stopPropagation(); onOpenEditor(p, 'truth'); }}>
                                                <Edit3 size={16} style={{ stroke: P.blue1 }} />
                                            </button>
                                            <span className="tooltip">Edit</span>
                                        </div>

                                        {/* [NEW] Delete Action */}
                                        <div className="tooltip-wrapper">
                                            <button
                                                className="btn-icon-outline"
                                                onClick={(e) => handleDelete(p.id, e)}
                                                style={{ borderColor: P.chipRed1 }}
                                            >
                                                <Trash2 size={16} className="text-red-500" style={{ stroke: P.red2 }} />
                                            </button>
                                            <span className="tooltip" style={{ background: P.red2 }}>Delete</span>
                                        </div>

                                        <div className="tooltip-wrapper">
                                            {/* [UX] Audits are now subtle secondary actions */}
                                            <button className="btn-icon-outline" onClick={(e) => { e.stopPropagation(); onSelectProfile(p); onAuditProfile(); }}>
                                                <Search size={16} style={{ stroke: P.muted }} />
                                            </button>
                                            <span className="tooltip">Run Audit</span>
                                        </div>

                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

// Add styles inside component for now (Self-contained)
const styleTag = document.createElement('style');
styleTag.innerHTML = `
    .smart-feed-scroll::-webkit-scrollbar-thumb { background: ${P.lineStrong}; border-radius: 4px; }
    .smart-feed-scroll::-webkit-scrollbar-thumb:hover { background: ${P.mutedSoft}; }

    .profile-row-modern {
        background: white;
        padding: 16px;
        border-radius: 10px;
        border: 1px solid ${P.line};
        box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        display: flex; 
        justify-content: space-between;
        align-items: center;
        cursor: pointer;
        transition: all 0.2s ease-in-out;
    }
    .profile-row-modern:hover {
        background-color: ${P.chipGreen1};
        border-color: ${P.chipGreen5};
    }

    .btn-icon-outline {
        width: 32px; height: 32px;
        border: 1px solid ${P.line};
        border-radius: 6px;
        background: white;
        display: flex; align-items: center; justify-content: center;
        cursor: pointer;
        transition: all 0.2s;
    }
    .btn-icon-outline:hover { background: ${P.canvas}; border-color: ${P.lineStrong}; }

    .btn-icon-dark {
        width: 32px; height: 32px;
        border: none;
        border-radius: 6px;
        background: ${P.ink};
        color: white;
        display: flex; align-items: center; justify-content: center;
        cursor: pointer;
        transition: all 0.2s;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }
    .btn-icon-dark:hover { background: ${P.inkDeep}; transform: translateY(-1px); }

    .tooltip-wrapper { position: relative; display: inline-block; }
    .tooltip {
        visibility: hidden;
        width: 80px;
        background-color: ${P.ink};
        color: ${P.surfaceShort};
        text-align: center;
        border-radius: 6px;
        padding: 5px 0;
        position: absolute;
        z-index: 10;
        bottom: 125%;
        left: 50%;
        margin-left: -40px;
        opacity: 0;
        transition: opacity 0.3s;
        font-size: 0.7rem;
        pointer-events: none;
    }
    .tooltip::after {
        content: "";
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: ${P.ink} transparent transparent transparent;
    }
    .tooltip-wrapper:hover .tooltip {
        visibility: visible;
        opacity: 1;
    }
`;
document.head.appendChild(styleTag);

export default OfficeLocationsList;
